import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  HostListener,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
  UiButtonComponent,
  UiFormComponent,
  UiPanelComponent,
} from '@template-ui/components';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import {
  WorkflowDefinitionAuthoringReferences,
  WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import {
  applyWorkflowStepProgress,
  workflowStepProgressConfigurationIssue,
  type WorkflowStepProgressDraft,
  type WorkflowStepProgressDraftValues,
  type WorkflowStepProgressEditIntent,
} from './workflow-step-progress-authoring';
import { statusScopeNode } from './workflow-status-authoring';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import type { WorkflowStepBypassDataIntent, WorkflowStepBypassDataOrigin, WorkflowStepBypassDataTarget } from './workflow-step-bypass-data-request';

export interface WorkflowStepProgressOwnedDocumentChange {
  document: WorkflowDefinitionDocument;
  nodeId: string;
  owner: string;
  source: string;
  view: 'overview' | 'bypass' | 'full';
  draft: WorkflowStepProgressDraft;
}
interface ProgressFormOrigin { generation: number; config: InputConfig[]; owner: string; source: string; activeView: string | null; view: 'overview' | 'bypass' | 'full'; }

@Component({
  selector: 'phms-workflow-step-progress-editor',
  imports: [CommonModule, UiButtonComponent, UiFormComponent, UiPanelComponent],
  templateUrl: './workflow-step-progress-editor.component.html',
  styleUrl: './workflow-step-progress-editor.component.scss',
})
export class WorkflowStepProgressEditorComponent implements OnChanges {
  private static nextInstanceId = 0;
  private readonly instanceId = `workflow-step-progress-${++WorkflowStepProgressEditorComponent.nextInstanceId}`;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() nodeId: string | null = null;
  @Input() canAuthor = false;
  @Input() interactionBlocked = false;
  @Input() resetVersion = 0;
  @Input() overviewMode = false;
  @Input() bypassMode = false;
  @Input() requireOwnerAcknowledgement = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() active: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Input() routingContracts: NonNullable<
    WorkflowDefinitionAuthoringReferences['routingContracts']
  > = [];
  @Input() referenceLoading = false;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly ownedDocumentChange = new EventEmitter<WorkflowStepProgressOwnedDocumentChange>();
  @Output() readonly bypassDataRequested = new EventEmitter<WorkflowStepBypassDataIntent>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });
  private readonly destroy = inject(DestroyRef);
  @ViewChild(UiFormComponent) private progressForm?: UiFormComponent;
  values: WorkflowStepProgressDraftValues = {
    startMode: '',
    allowBypass: false,
    bypassCondition: '',
    outcomeCode: '',
    processDataKey: '',
    reason: '',
  };
  formConfig: InputConfig[] = [];
  pending = false;
  error = '';
  message = '';
  private context = '';
  private bufferNodeId: string | null = null;
  private baseline = '';
  private baselineReference = '';
  private ownerSnapshot = '';
  private presentationSnapshot = '';
  private renderedView: string | null = null;
  private generation = 0;
  private focusRequest = 0;
  private editIntent: WorkflowStepProgressEditIntent = 'progress-policy';
  private applying = false;
  retainedIssue = '';
  retainedConfiguration: unknown = null;

  get ownerView(): 'overview' | 'bypass' | 'full' { return this.bypassMode ? 'bypass' : this.overviewMode ? 'overview' : 'full'; }
  get draftSnapshot(): WorkflowStepProgressDraft { return { values: this.draftValues(), intent: this.editIntent }; }
  get pendingProgressView(): 'overview' | 'bypass' {
    return !['manual', 'assigned_open'].includes(this.values.startMode) || this.editIntent === 'start-mode' ? 'overview' : 'bypass';
  }
  get headingId(): string { return `${this.instanceId}-${encodeURIComponent(this.nodeId ?? 'unselected')}-title`; }
  get formInstanceId(): string { return `${this.instanceId}-${encodeURIComponent(this.nodeId ?? 'unselected')}-settings`; }
  get editorView() { return { config: this.formConfig, origin: { generation: this.generation, config: this.formConfig, owner: this.ownerKey(),
    source: JSON.stringify(this.document), activeView: this.activeView?.() ?? null, view: this.ownerView } satisfies ProgressFormOrigin }; }
  get retainedBypass(): unknown { const value = this.rawConfiguration(this.document); return this.isRecord(value) ? value['bypass'] ?? null : null; }

  bypassDataRequestContext(): string | null {
    if (!this.bypassDataEntryCurrent()) return null;
    return JSON.stringify([this.instanceId, this.bufferNodeId, this.generation, this.ownerView, this.renderedView]);
  }
  get bypassDataView() {
    const context = this.bypassDataRequestContext();
    return context ? { context, origin: this.editorView.origin, draft: this.draftSnapshot,
      reviewKey: this.values.processDataKey && this.dataKeyCount(this.values.processDataKey) <= 1 ? this.values.processDataKey : null } : null;
  }
  requestBypassData(target: WorkflowStepBypassDataTarget, origin: ProgressFormOrigin, originContext: string, draft: WorkflowStepProgressDraft): void {
    if (!this.nativeCurrent(origin) || !originContext || originContext !== this.bypassDataRequestContext()
      || JSON.stringify(draft) !== JSON.stringify(this.draftSnapshot) || !this.nodeId || !this.isRecord(target)) return;
    if (target.kind === 'add-boolean' ? Object.keys(target).length !== 1
      : target.kind !== 'review-field' || Object.keys(target).length !== 2 || typeof target.key !== 'string' || !target.key
        || target.key !== this.values.processDataKey || this.dataKeyCount(target.key) > 1) return;
    this.bypassDataRequested.emit({ nodeId: this.nodeId, owner: this.ownerKey(), source: JSON.stringify(this.currentDocument()),
      view: 'bypass', originContext, draft: this.draftSnapshot, target: { ...target } });
  }
  /** A hidden owner's actual baseline proof; native entry and later Return have different presentation requirements. */
  readBypassDataOrigin(): WorkflowStepBypassDataOrigin | null {
    const latest = this.currentDocument();
    if (!this.ownerCurrent() || !latest || !Array.isArray(latest.nodes) || latest.nodes.some(node => !this.isRecord(node)) || !this.nodeId || this.nodeId !== this.bufferNodeId
      || this.presentationSnapshot !== this.presentationKey() || this.contextFor(latest) !== this.context
      || this.referenceFor(latest) !== this.baselineReference || !statusScopeNode(latest, this.nodeId)
      || JSON.stringify(this.rawConfiguration(latest)) !== this.baseline) return null;
    let state: WorkflowStepBypassDataOrigin['state'] = 'clean';
    if (this.pending) {
      try { this.pendingDocument(latest); state = 'valid'; } catch { state = 'invalid'; }
    }
    return { nodeId: this.nodeId, reference: this.baselineReference, configuration: this.baseline, draft: this.draftSnapshot, state };
  }
  private dataKeyCount(key: string): number {
    const fields = this.currentDocument()?.processData?.fields;
    return Array.isArray(fields) ? fields.filter(field => this.isRecord(field) && field.key === key).length : 0;
  }
  private bypassDataEntryCurrent(): boolean {
    if (this.ownerView !== 'bypass' || this.disabled || this.referenceLoading || !this.values.allowBypass || !this.nativeCurrent()) return false;
    const matches = Array.isArray(this.routingContracts) ? this.routingContracts.filter(contract => this.isRecord(contract)
      && `${contract.code}@${contract.version}` === this.values.bypassCondition) : [];
    const contract = matches.length === 1 ? matches[0] : null;
    return !!contract && contract.code === 'workflow.process-flag' && Number.isInteger(contract.version) && contract.version > 0
      && contract.supportsStepBypass === true && Array.isArray(contract.outcomeCodes)
      && contract.outcomeCodes.every(code => typeof code === 'string' && !!code.trim())
      && new Set(contract.outcomeCodes).size === contract.outcomeCodes.length && contract.outcomeCodes.includes('bypass');
  }

  get disabled(): boolean {
    return (
      !this.canAuthor ||
      this.interactionBlocked ||
      !this.document ||
      !this.nodeId ||
      !this.ownerCurrent() || !!this.retainedIssue ||
      !statusScopeNode(this.document, this.nodeId)
    );
  }

  get usesLegacyProgress(): boolean {
    return (
      !!this.document &&
      !!this.nodeId &&
      this.rawConfiguration(this.document) === null
    );
  }

  get bypassConditions(): NonNullable<
    WorkflowDefinitionAuthoringReferences['routingContracts']
  > {
    const contracts = Array.isArray(this.routingContracts) ? this.routingContracts : [];
    return contracts.filter(
      (item) => this.isRecord(item) && item.supportsStepBypass === true && typeof item.code === 'string' && Number.isInteger(item.version)
        && contracts.filter(candidate => this.isRecord(candidate) && candidate.code === item.code && candidate.version === item.version).length === 1,
    );
  }

  get needsProcessFlag(): boolean {
    return (
      this.bypassConditions.find(
        (item) =>
          `${item.code}@${item.version}` === this.values.bypassCondition,
      )?.code === 'workflow.process-flag'
    );
  }

  get hasProcessFlags(): boolean {
    return (
      this.processFlags.length > 0
    );
  }
  private get processFlags() {
    const fields = Array.isArray(this.document?.processData?.fields) ? this.document.processData.fields : [];
    return fields.filter(field => this.isRecord(field) && field.type === 'boolean' && typeof field.key === 'string'
      && fields.filter(candidate => this.isRecord(candidate) && candidate.key === field.key).length === 1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const view = this.activeView?.() ?? null;
    if ((this.activeViewState === undefined || this.activeViewState === view) && this.renderedView !== view) {
      this.renderedView = view; this.generation++; this.focusRequest++;
    }
    const context = this.contextFor(this.document);
    if (this.pending && changes['document'] && this.viewContext && this.presentationSnapshot === this.presentationKey()
      && context === this.context && this.referenceFor(this.document) === this.baselineReference
      && JSON.stringify(this.rawConfiguration(this.document)) === this.baseline) this.ownerSnapshot = this.ownerKey();
    if ((context !== this.context && (!this.pending || !this.ownerContext || changes['resetVersion'])) || (!this.pending && changes['document'])) {
      this.context = context;
      this.reset();
    } else if (!this.pending && (changes['ownerContext'] || changes['activeViewState'] && this.activeViewState === view)) {
      this.ownerSnapshot = this.ownerKey(); this.presentationSnapshot = this.presentationKey();
    }
    if (changes['routingContracts'] || changes['referenceLoading'] || changes['overviewMode'] || changes['bypassMode'] || changes['document']) {
      if (!this.pending) this.editIntent = this.ownerView === 'full' ? 'progress-policy' : 'start-mode';
      this.rebuildForm();
    }
  }

  onValuesChange(value: unknown, origin?: ProgressFormOrigin): void {
    if (!this.nativeCurrent(origin)) {
      this.values = { ...this.values };
      this.progressForm?.theFormGroup?.patchValue(this.values, { emitEvent: false });
      return;
    }
    if (this.disabled || !value || typeof value !== 'object') return;
    const incoming = value as Partial<typeof this.values>;
    const previous = this.draftValues();
    const next = { ...previous };
    for (const key of [
      'startMode',
      'bypassCondition',
      'outcomeCode',
      'processDataKey',
      'reason',
    ] as const)
      if ((this.ownerView === 'full' || this.ownerView === 'overview' && key === 'startMode' || this.ownerView === 'bypass' && key !== 'startMode') && typeof incoming[key] === 'string') next[key] = incoming[key];
    if (this.ownerView !== 'overview' && typeof incoming.allowBypass === 'boolean')
      next.allowBypass = incoming.allowBypass;
    if (next.bypassCondition !== previous.bypassCondition) {
      next.outcomeCode =
        this.bypassConditions.find(
          (item) => `${item.code}@${item.version}` === next.bypassCondition,
        )?.code === 'workflow.process-flag'
          ? 'bypass'
          : '';
      next.processDataKey = '';
    }
    if (JSON.stringify(next) === JSON.stringify(previous)) return;
    if (!this.pending) this.editIntent = this.ownerView === 'full' ? 'progress-policy' : 'start-mode';
    if (this.ownerView === 'full' || (Object.keys(next) as (keyof WorkflowStepProgressDraftValues)[]).some(key => key !== 'startMode' && next[key] !== previous[key]))
      this.editIntent = 'progress-policy';
    this.values = next;
    this.pending = true;
    this.error = '';
    this.message = '';
    this.pendingChangesChange.emit(true);
    if (
      next.allowBypass !== previous.allowBypass ||
      next.bypassCondition !== previous.bypassCondition ||
      (this.needsProcessFlag && next.outcomeCode !== previous.outcomeCode)
    )
      this.rebuildForm();
  }

  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (this.applying) return false;
    if (!this.pending) return true;
    if (!this.ownerCurrent()) return this.fail('The draft or author context changed. Your step progress input is retained; discard its scoped input to review the current source.');
    const live = this.currentDocument();
    const latest = latestDocument || live;
    if (this.requireOwnerAcknowledgement && (!live || JSON.stringify(latest) !== JSON.stringify(live)))
      return this.fail('The current draft changed. Your step progress input is retained.');
    if (this.disabled || !latest || !this.nodeId) return false;
    const node = statusScopeNode(latest, this.nodeId);
    if (
      !node ||
      this.contextFor(latest) !== this.context ||
      this.referenceFor(latest) !== this.baselineReference ||
      JSON.stringify(node.stepProgressConfiguration ?? null) !== this.baseline
    )
      return this.fail(
        'These step progress settings changed while you were editing. Cancel changes and review the latest settings.',
      );
    let next: WorkflowDefinitionDocument;
    try { next = this.pendingDocument(latest); }
    catch (error) { return this.fail(error instanceof Error ? error.message : 'Review step progress input.'); }
    const owned: WorkflowStepProgressOwnedDocumentChange = { document: next, nodeId: this.nodeId, owner: this.ownerKey(), source: JSON.stringify(latest), view: this.ownerView, draft: this.draftSnapshot };
    let acknowledged = next;
    if (this.requireOwnerAcknowledgement) {
      const namespace = this.presentationKey(), draft = JSON.stringify(this.draftSnapshot), nodeId = this.nodeId, owner = owned.owner, expected = JSON.stringify(next);
      this.applying = true;
      try { this.ownedDocumentChange.emit(owned); } finally { this.applying = false; }
      const current = this.currentDocument();
      if (!current || current === live || owner !== this.ownerKey() || namespace !== this.presentationKey() || nodeId !== this.nodeId
        || draft !== JSON.stringify(this.draftSnapshot) || JSON.stringify(current) !== expected)
        return this.fail('Step progress was not applied to the current draft. Your input is retained; review the current owner and source.');
      acknowledged = current;
    }
    this.document = acknowledged;
    this.baseline = JSON.stringify(statusScopeNode(next, this.nodeId)?.stepProgressConfiguration ?? null);
    this.pending = false;
    this.pendingChangesChange.emit(false);
    this.documentChange.emit(next);
    if (!this.requireOwnerAcknowledgement) this.ownedDocumentChange.emit(owned);
    this.editIntent = this.ownerView === 'full' ? 'progress-policy' : 'start-mode';
    this.error = '';
    this.message =
      'Step progress applied locally. Save draft to keep these changes.';
    return true;
  }

  hasPendingChanges(): boolean { return this.pending; }
  private referenceFor(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference]);
  }
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'step-progress', label: 'step progress input',
      state: () => {
        if (!this.pending) return 'clean';
        const latest = latestDocument(), node = latest && this.nodeId ? statusScopeNode(latest, this.nodeId) : null;
        if (!this.ownerCurrent() || !latest || !node || this.contextFor(latest) !== this.context || this.referenceFor(latest) !== this.baselineReference ||
          JSON.stringify(node.stepProgressConfiguration ?? null) !== this.baseline) return 'stale';
        if (this.disabled) return 'invalid';
        try { this.pendingDocument(latest); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const latest = latestDocument();
        return JSON.stringify([this.pending, this.draftValues(), this.editIntent, this.context, this.baseline, this.baselineReference,
          this.contextFor(latest), this.referenceFor(latest), latest && this.nodeId ? statusScopeNode(latest, this.nodeId)?.stepProgressConfiguration : null,
          latest?.processData, this.routingContracts, this.referenceLoading, this.canAuthor, this.interactionBlocked, this.ownerSnapshot, this.ownerKey()]);
      },
      apply: () => { const latest = latestDocument(); return latest ? this.applyPendingChanges(latest) : !this.pending; },
      discard: () => { this.document = latestDocument(); this.context = this.contextFor(this.document); this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private pendingDocument(latest: WorkflowDefinitionDocument): WorkflowDefinitionDocument {
    if (!this.nodeId) throw new Error('Choose one unambiguous editable work step.');
    return applyWorkflowStepProgress(latest, this.nodeId, this.draftValues(), this.editIntent,
      { contracts: this.routingContracts, loading: this.referenceLoading });
  }

  discardPendingChanges(): void {
    this.document = this.currentDocument();
    this.context = this.contextFor(this.document);
    this.reset();
  }
  private reset(): void {
    this.ownerSnapshot = this.ownerKey(); this.presentationSnapshot = this.presentationKey(); this.generation++; this.focusRequest++;
    this.bufferNodeId = this.nodeId;
    this.retainedConfiguration = this.rawConfiguration(this.document);
    this.retainedIssue = this.configurationIssue(this.retainedConfiguration);
    const configuration =
      this.document && this.nodeId
        ? statusScopeNode(this.document, this.nodeId)?.stepProgressConfiguration
        : null;
    this.baseline = JSON.stringify(configuration ?? null);
    this.baselineReference = this.referenceFor(this.document);
    this.values = {
      startMode: typeof configuration?.startMode === 'string' ? configuration.startMode : '',
      allowBypass: !!configuration?.bypass,
      bypassCondition: configuration?.bypass && typeof configuration.bypass.conditionCode === 'string' && typeof configuration.bypass.conditionVersion === 'number'
        ? `${configuration.bypass.conditionCode}@${configuration.bypass.conditionVersion}`
        : '',
      outcomeCode: typeof configuration?.bypass?.outcomeCode === 'string' ? configuration.bypass.outcomeCode : '',
      processDataKey: typeof configuration?.bypass?.processDataKey === 'string' ? configuration.bypass.processDataKey : '',
      reason: typeof configuration?.bypass?.reason === 'string' ? configuration.bypass.reason : '',
    };
    this.editIntent = this.ownerView === 'full' ? 'progress-policy' : 'start-mode';
    this.pending = false;
    this.error = '';
    this.message = '';
    this.pendingChangesChange.emit(false);
    this.rebuildForm();
  }

  focusPendingChanges(reveal = false, path?: string): void {
    const request = ++this.focusRequest, owner = this.ownerKey(), source = JSON.stringify(this.currentDocument()), view = this.activeView?.() ?? null,
      generation = this.generation, focused = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.element.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.focusRequest || !this.presentationActive() || !host.isConnected || owner !== this.ownerKey() || source !== JSON.stringify(this.currentDocument())
        || view !== (this.activeView?.() ?? null) || generation !== this.generation) return;
      if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const field = path?.includes('processDataKey') ? 'processDataKey' : path?.includes('condition') ? 'bypassCondition'
        : path?.includes('outcomeCode') ? 'outcomeCode' : path?.includes('reason') ? 'reason' : path?.includes('startMode') ? 'startMode'
          : path?.includes('bypass') ? 'allowBypass' : null;
      const fallback = this.ownerView === 'full'
        ? host.querySelector<HTMLElement>('[role="alert"][tabindex],mat-select,input,textarea,button')
        : host.querySelector<HTMLElement>('[role="alert"][tabindex]') ?? host.querySelector<HTMLElement>('mat-select,input,textarea,button');
      const target = (field ? host.querySelector<HTMLElement>(`[control-name="${field}"] mat-select,[control-name="${field}"] input,[control-name="${field}"] textarea`) : null)
        ?? fallback ?? host.querySelector<HTMLElement>('h4[tabindex]');
      if (target && !target.closest('[hidden],[inert]') && !target.matches(':disabled')) {
        const focusedRequest = request + (active === target ? 0 : 1);
        target.focus({ preventScroll: true });
        if (reveal && this.ownerView !== 'full' && host.ownerDocument.activeElement === target && focusedRequest === this.focusRequest && this.presentationActive()
          && owner === this.ownerKey() && source === JSON.stringify(this.currentDocument()) && view === (this.activeView?.() ?? null)
          && generation === this.generation && target.isConnected && !target.closest('[hidden],[inert]')) target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      }
    });
  }
  @HostListener('focusin') rememberNewFocus(): void { this.focusRequest++; }
  applyVisibleChanges(): void { if (this.nativeCurrent()) this.applyPendingChanges(); }
  discardVisibleChanges(): void { if (this.nativeCurrent()) this.discardPendingChanges(); }
  capturePendingProgressInputs(): readonly { name: string; label: string; value: string }[] {
    return !this.pending ? [] : [{ name: `step-progress-input:${encodeURIComponent(this.bufferNodeId ?? 'unselected')}`, label: `Unapplied step progress for ${this.bufferNodeId ?? 'unselected'}`,
      value: JSON.stringify({ format: 'phms.workflow-step-progress-input', version: 1, nodeId: this.bufferNodeId, values: this.draftValues(), intent: this.editIntent,
        configuration: JSON.parse(this.baseline), originalConfiguration: this.baseline, originalReference: this.baselineReference, context: this.context,
        originalOwner: this.ownerSnapshot, originalNamespace: this.presentationSnapshot }) }];
  }
  private draftValues(): WorkflowStepProgressDraftValues {
    // UiForm decorates its input object with proxy metadata. Only these six
    // authoring values belong in the strict writer, owned event and recovery copy.
    return { startMode: this.values.startMode, allowBypass: this.values.allowBypass, bypassCondition: this.values.bypassCondition,
      outcomeCode: this.values.outcomeCode, processDataKey: this.values.processDataKey, reason: this.values.reason };
  }
  private isRecord(value: unknown): value is Record<string, unknown> { return !!value && typeof value === 'object' && !Array.isArray(value); }
  private rawConfiguration(document: WorkflowDefinitionDocument | null): unknown {
    return document && this.nodeId ? statusScopeNode(document, this.nodeId)?.stepProgressConfiguration ?? null : null;
  }
  private configurationIssue(value: unknown): string { return workflowStepProgressConfigurationIssue(value); }
  private ownerKey(): string { return JSON.stringify(this.ownerContext?.() ?? null); }
  private ownerCurrent(): boolean { return !this.destroy.destroyed && this.ownerSnapshot === this.ownerKey(); }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private presentationKey(): string { return JSON.stringify([this.contextFor(this.document), this.viewContext?.() ?? this.ownerContext?.() ?? null]); }
  private presentationActive(): boolean {
    return this.ownerCurrent() && (!this.active || this.active()) && (!this.activeView || this.activeView() !== null && this.activeView() === this.renderedView
      && (this.activeViewState === undefined || this.activeViewState === this.renderedView))
      && !this.interactionBlocked && !this.ownerViews?.focusSuspended() && !this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]');
  }
  private nativeCurrent(origin?: ProgressFormOrigin): boolean {
    return this.presentationActive() && JSON.stringify(this.document) === JSON.stringify(this.currentDocument())
      && this.nodeId === this.bufferNodeId && this.contextFor(this.currentDocument()) === this.context
      && JSON.stringify(this.rawConfiguration(this.currentDocument())) === this.baseline
      && (!origin || origin.generation === this.generation && origin.config === this.formConfig && origin.view === this.ownerView
        && origin.owner === this.ownerKey() && origin.source === JSON.stringify(this.currentDocument()) && origin.activeView === (this.activeView?.() ?? null));
  }

  private contextFor(document: WorkflowDefinitionDocument | null): string {
    return `${document?.workflowCode}/${document?.definitionVersionCode}/${this.nodeId}/${this.resetVersion}`;
  }

  private fail(message: string): false {
    this.error = message;
    this.focusPendingChanges();
    return false;
  }

  private rebuildForm(): void {
    this.generation++;
    this.formConfig = [];
    if (this.ownerView !== 'bypass') this.formConfig.push({
      name: 'startMode', label: 'Work starts', type: EzFormInputType.SELECT, width: '1x',
      placeholder: 'Choose how staff start this step',
      options: [
        { value: 'assigned_open', viewValue: 'Assigned person opens the staff page, or staff choose Start work' },
        { value: 'manual', viewValue: 'Staff choose Start work' },
      ],
      validation: [Validators.required],
    });
    if (this.ownerView === 'overview') return;
    this.formConfig.push({
      name: 'allowBypass', label: this.ownerView === 'bypass' ? 'Allow predefined bypass' : 'Allow a predefined condition to bypass this step',
      type: EzFormInputType.CHECKBOX, width: '1x',
    });
    if (!this.values.allowBypass) return;
    const selected = this.bypassConditions.find(item => `${item.code}@${item.version}` === this.values.bypassCondition);
    const conditions = this.bypassConditions.map(item => ({
      value: `${item.code}@${item.version}`,
      viewValue: `${item.displayName || friendlyCode(item.code)}${this.bypassConditions.filter(candidate => candidate.code === item.code).length > 1 ? ` (version ${item.version})` : ''}`,
    }));
    if (this.values.bypassCondition && !selected) conditions.push({ value: this.values.bypassCondition,
      viewValue: 'Saved condition is unavailable — choose an installed condition' });
    this.formConfig.push({
      name: 'bypassCondition', label: this.ownerView === 'bypass' ? 'Bypass condition' : 'Bypass when this condition is checked', type: EzFormInputType.SELECT,
      width: '1x', options: conditions, validation: [Validators.required],
    });
    if (this.needsProcessFlag) {
      const flags = this.processFlags.map(field => ({ value: field.key, viewValue: field.label }));
      if (this.values.processDataKey && !flags.some(field => field.value === this.values.processDataKey))
        flags.push({ value: this.values.processDataKey, viewValue: 'Saved yes/no process value is unavailable — choose a declared value' });
      this.formConfig.push({ name: 'processDataKey', label: 'Yes/no process value', type: EzFormInputType.SELECT,
        width: '1x', options: flags, validation: [Validators.required] });
    }
    if (!this.needsProcessFlag || this.values.outcomeCode !== 'bypass') {
      const outcomes = this.needsProcessFlag ? [{ value: 'bypass', viewValue: 'Yes — bypass this step' }]
        : (Array.isArray(selected?.outcomeCodes) ? selected.outcomeCodes.filter(code => typeof code === 'string') : [])
          .map(code => ({ value: code, viewValue: friendlyCode(code) }));
      if (this.values.outcomeCode && !outcomes.some(outcome => outcome.value === this.values.outcomeCode))
        outcomes.push({ value: this.values.outcomeCode, viewValue: this.needsProcessFlag
          ? 'Saved result is unsupported — choose Yes' : 'Saved result is unavailable — choose a supported result' });
      this.formConfig.push({ name: 'outcomeCode', label: this.needsProcessFlag ? this.ownerView === 'bypass' ? 'Correct saved result' : 'Correct the saved bypass result' : 'And the result is',
        type: EzFormInputType.SELECT, width: '1x', options: outcomes, validation: [Validators.required] });
    }
    this.formConfig.push({ name: 'reason', label: this.ownerView === 'bypass' ? 'History explanation' : 'Explanation shown in the process history', type: EzFormInputType.TEXTAREA,
      width: '1x', placeholder: 'For example: A current document is already on file.', validation: [Validators.required, Validators.maxLength(240)] });
  }

}

function friendlyCode(code: string): string {
  const words = code.replace(/[._-]+/g, ' ');
  return words.charAt(0).toLocaleUpperCase() + words.slice(1);
}
